import type { RequestListSummary } from "@gw/shared-types";
import { InfoTooltip } from "@/components/InfoTooltip";

export type QuickFilter = "slow" | "inprogress" | null;

interface Props {
  summary: RequestListSummary;
  activeQuickFilter: QuickFilter;
  onQuickFilter: (q: QuickFilter) => void;
  unit: "bd" | "cd";
}

// Tooltip copy is written for end users — plain language, no implementation jargon. Keep
// aligned with RequestService.ComputeSummary; "Completed" / "In progress" here refer to the
// Status column the user sees on the table (CalculatedStatus on the wire).
const TIP_TOTAL =
  "Count of requests in the current view, after the filters in the rail.";
const TIP_MEDIAN =
  "Median total duration across Completed requests with measurable duration. Requests still in progress are excluded so the number reflects real cycle time.";
const TIP_AVERAGE =
  "Average (mean) total duration across Completed requests with measurable duration — the same set as the Median. Requests still in progress are excluded so the number reflects real cycle time.";
const TIP_P90 =
  "P90 of Completed durations — same set as Median. Useful for spotting the long tail of slow requests.";
const TIP_SLOW =
  "Count where slowness is Slow (≥ 1.25× peer median) or Critical (≥ 2.0× peer median). Only Completed requests are eligible — in-progress work is never marked Slow or Critical. Click the tile to filter.";
const TIP_INPROGRESS =
  "Count of requests still in progress — work orders awaiting approval, plus intake requests still being processed. Click the tile to filter to that set.";

export function SummaryTiles({ summary, activeQuickFilter, onQuickFilter, unit }: Props) {
  const { total, completed, inProgress, slowOrCriticalCount, measuredCompleted, medianBd, meanBd, p90Bd } =
    summary;

  // Median/P90 are computed over completed rows with BD > 0 only — see RequestListSummary docs.
  // When zero rows have measurable duration, fall back to a "—" so we don't surface a misleading 0.
  const hasMeasured = measuredCompleted > 0;
  const measuredSubtitle = hasMeasured
    ? `${measuredCompleted.toLocaleString()} of ${completed.toLocaleString()} measured`
    : `0 of ${completed.toLocaleString()} measured`;

  return (
    <div className="summary">
      <div className="tile">
        <div className="tile-label">Total requests</div>
        <div className="tile-value">{total.toLocaleString()}</div>
        <div className="tile-sub">in current view</div>
        <InfoTooltip text={TIP_TOTAL} />
      </div>
      <div className="tile">
        <div className="tile-label">Median total duration</div>
        <div className="tile-value">
          {hasMeasured ? medianBd.toFixed(2) : "—"}
          {hasMeasured && <span className="unit">{unit}</span>}
        </div>
        <div className="tile-sub">{measuredSubtitle}</div>
        <InfoTooltip text={TIP_MEDIAN} />
      </div>
      <div className="tile">
        <div className="tile-label">Average total duration</div>
        <div className="tile-value">
          {hasMeasured ? meanBd.toFixed(2) : "—"}
          {hasMeasured && <span className="unit">{unit}</span>}
        </div>
        <div className="tile-sub">{measuredSubtitle}</div>
        <InfoTooltip text={TIP_AVERAGE} />
      </div>
      <div className="tile">
        <div className="tile-label">P90 total duration</div>
        <div className="tile-value">
          {hasMeasured ? p90Bd.toFixed(2) : "—"}
          {hasMeasured && <span className="unit">{unit}</span>}
        </div>
        <div className="tile-sub">{measuredSubtitle}</div>
        <InfoTooltip text={TIP_P90} />
      </div>
      <button
        type="button"
        className={"tile clickable warn " + (activeQuickFilter === "slow" ? "active" : "")}
        onClick={() => onQuickFilter(activeQuickFilter === "slow" ? null : "slow")}
      >
        <div className="tile-label">Slow + critical</div>
        <div className="tile-value">{slowOrCriticalCount}</div>
        <div className="tile-sub">click to filter →</div>
        <InfoTooltip text={TIP_SLOW} />
      </button>
      <button
        type="button"
        className={"tile clickable " + (activeQuickFilter === "inprogress" ? "active" : "")}
        onClick={() => onQuickFilter(activeQuickFilter === "inprogress" ? null : "inprogress")}
      >
        <div className="tile-label">In progress</div>
        <div className="tile-value">{inProgress}</div>
        <div className="tile-sub">click to filter →</div>
        <InfoTooltip text={TIP_INPROGRESS} />
      </button>
    </div>
  );
}
